'use client';

import { AlertTriangle, CircleCheckBig, ExternalLink, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  buildQuickStartGoTarget,
  buildQuickStartLearnMoreUrl,
} from '@/components/setup/quick-start/quickStartNavigation';
import { Button } from '@/components/ui/button';
import type { QuickStartTaskResult } from '@/lib/api/setup.types';
import { cn } from '@/lib/utils';

type QuickStartPathTaskProps = {
  task: QuickStartTaskResult;
  /** The single required step the merchant should tackle next (the "NOW" row). */
  isActive: boolean;
  orgSlug: string;
  storeId: string | null;
  helpBase: string;
};

type TaskVariant = 'active' | 'todo' | 'completed' | 'locked';

const resolveVariant = (status: QuickStartTaskResult['status'], isActive: boolean): TaskVariant => {
  if (status === 'completed') return 'completed';
  if (status === 'locked') return 'locked';
  return isActive ? 'active' : 'todo';
};

const TaskIcon = ({ variant }: { variant: TaskVariant }) => {
  if (variant === 'completed') {
    return (
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center">
        <CircleCheckBig className="text-primary size-[1.375rem]" strokeWidth={2} aria-hidden />
      </span>
    );
  }
  if (variant === 'locked') {
    return (
      <span className="bg-muted text-muted-foreground mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full">
        <Lock className="size-3.5" aria-hidden />
      </span>
    );
  }
  return (
    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-200/80 dark:bg-orange-500/15 dark:ring-orange-500/25">
      <AlertTriangle
        className="size-3.5 fill-orange-500 text-orange-500 dark:fill-orange-400 dark:text-orange-400"
        aria-hidden
      />
    </span>
  );
};

export function QuickStartPathTask({ task, isActive, orgSlug, storeId, helpBase }: QuickStartPathTaskProps) {
  const { t } = useTranslation('setup');
  const variant = resolveVariant(task.status, isActive);
  const goTarget = buildQuickStartGoTarget(task, orgSlug, storeId);
  const learnMore = buildQuickStartLearnMoreUrl(helpBase, task.learnMorePath);
  const showLearnMore = Boolean(helpBase) && (variant === 'active' || variant === 'todo');

  return (
    <article
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-4 transition-colors sm:flex-row sm:items-center sm:gap-4',
        variant === 'active' &&
          'border-l-[3px] border-orange-200/80 border-l-orange-400 bg-orange-50/70 dark:border-orange-500/30 dark:border-l-orange-500/70 dark:bg-orange-500/[0.07]',
        variant === 'todo' && 'border-border bg-card hover:border-border/60',
        variant === 'completed' && 'border-border bg-card',
        variant === 'locked' && 'border-border/60 bg-muted/20',
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <TaskIcon variant={variant} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3
              className={cn(
                'text-sm',
                variant === 'active' && 'text-foreground font-semibold',
                variant === 'todo' && 'text-foreground font-medium',
                variant === 'completed' &&
                  'text-muted-foreground decoration-primary/40 font-medium line-through',
                variant === 'locked' && 'text-muted-foreground font-medium',
              )}
            >
              {t(task.titleKey)}
            </h3>
            {variant === 'active' ? (
              <span className="rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-white">
                {t('quickStart.badge.now')}
              </span>
            ) : null}
            {variant === 'completed' ? (
              <span className="text-primary text-xs font-medium">
                {t('quickStart.statusVisual.completed')}
              </span>
            ) : null}
            {variant === 'locked' ? (
              <span className="text-muted-foreground text-xs font-medium">
                {t('quickStart.statusVisual.locked')}
              </span>
            ) : null}
          </div>
          <p className="text-muted-foreground mt-1 text-[13px] leading-relaxed">{t(task.descriptionKey)}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-3">
        {showLearnMore ? (
          <a
            href={learnMore}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium"
          >
            {t('quickStart.learnMore')}
            <ExternalLink className="size-3" />
          </a>
        ) : null}
        <TaskAction variant={variant} href={goTarget?.href ?? null} />
      </div>
    </article>
  );
}

const TaskAction = ({ variant, href }: { variant: TaskVariant; href: string | null }) => {
  const { t } = useTranslation('setup');

  if (variant === 'locked' || !href) {
    return (
      <Button size="sm" variant="secondary" disabled className="h-9 min-w-[4.75rem] px-4">
        {variant === 'completed' ? t('quickStart.review') : t('quickStart.go')}
      </Button>
    );
  }

  if (variant === 'completed') {
    return (
      <Button size="sm" variant="outline" asChild className="h-9 min-w-[4.75rem] px-4">
        <Link to={href}>{t('quickStart.review')}</Link>
      </Button>
    );
  }

  if (variant === 'active') {
    return (
      <Button
        size="sm"
        asChild
        className="h-9 min-w-[4.75rem] bg-orange-500 px-4 text-white hover:bg-orange-600 dark:bg-orange-500 dark:hover:bg-orange-600"
      >
        <Link to={href}>{t('quickStart.go')}</Link>
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      asChild
      className="h-9 min-w-[4.75rem] border-orange-300 px-4 text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-500/40 dark:text-orange-300 dark:hover:bg-orange-500/10 dark:hover:text-orange-200"
    >
      <Link to={href}>{t('quickStart.go')}</Link>
    </Button>
  );
};
